import React, { useState, useEffect } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useLocation } from 'react-router-dom'
import pathMap from 'src/paths/pathMap'
import ConfirmDialog from 'src/views/common/ConfirmDialog'
import MPCIApiUrls from '../Common/services/MPCIApiService'
import MpciApiClient from '../Common/services/MpciApiClient'
import UaeMpciApiUrls from 'src/apiUrls/UaeMpciApiUrls'
import FormProperties from './FormProperties'
import makeValidationSchema from './MakeValidationSchema'
import ParentCard from 'src/components/era-package/shared/ParentCard'
import { Box, CircularProgress } from '@material-ui/core'

function AddPage({ history, showNotification }) {
  const loc = useLocation()
  const [discardDialog, setDiscardDialog] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [cloneLoading, setCloneLoading] = useState(false)

  const cloneId = new URLSearchParams(loc.search).get('cloneId')

  const initialValues = {
    ...FormProperties.formikProps.initialValues,
    ...(loc.state ? { ...loc.state, functionType: 'NEW', billType: 'M' } : {})
  }

  const methods = useForm({ defaultValues: initialValues, resolver: yupResolver(makeValidationSchema()) })

  // If cloneId present — call POST /master-bill/clone/{id} and pre-fill form
  useEffect(() => {
    if (!cloneId) return
    setCloneLoading(true)
    MpciApiClient.post(
      `${UaeMpciApiUrls.MASTER_BILL_CLONE}/${cloneId}`,
      {},
      (data) => {
        setCloneLoading(false)
        if (data) {
          const formValues = FormProperties.toClientDataFormat(data)
          methods.reset({ ...formValues, id: null, functionType: 'NEW', reasonForUpdatingFiling: '', billType: 'M', status: '' })
        }
      },
      () => {
        setCloneLoading(false)
        showNotification('error', 'Failed to clone Master Bill. Please try again.')
      }
    )
  }, [cloneId]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveDraft = () => {
    setSubmitting(true)
    const payload = FormProperties.toServerDataFormat(methods.getValues())
    MpciApiClient.post(
      MPCIApiUrls.MASTER_BILL.POST,
      payload,
      (data) => {
        setSubmitting(false)
        showNotification('success', 'Master Bill saved as draft.')
        const id = data?.id
        if (id) {
          history.push(`${pathMap.UAE_MPCI_MASTER_BILL}/edit/${id}`)
        } else {
          history.push(pathMap.UAE_MPCI_MASTER_BILL_LIST_VIEW)
        }
      },
      (err) => {
        setSubmitting(false)
        const errMsg = (err && err.status && err.message && err.status !== 500) ? `Error: ${err.status} - ${err.message}` : 'Failed to save draft. Please try again.'
        showNotification('error', errMsg)
      }
    )
  }

    const handleSubmitToNaic = methods.handleSubmit((formValues) => {
    setSubmitting(true)
    const payload = FormProperties.toServerDataFormat(formValues)
    // Step 1 — POST /create
    MpciApiClient.post(
      MPCIApiUrls.MASTER_BILL.POST,
      payload,
      (createdData) => {
        const id = createdData?.id
        if (!id) {
          setSubmitting(false)
          showNotification('error', 'Record created but no ID returned.')
          return
        }
        // Step 2 — PUT /submit/{id}
        // Use createdData from server (contains all child IDs assigned during create).
        // Contract: "include 'id' at root and on all existing child records"
        MpciApiClient.putById(
          UaeMpciApiUrls.MASTER_BILL_SUBMIT,
          id,
          createdData,
          [],
          () => {
            setSubmitting(false)
            showNotification('success', 'Master Bill submitted to NAIC successfully.')
            history.push(pathMap.UAE_MPCI_MASTER_BILL_LIST_VIEW)
          },
          (err) => {
            setSubmitting(false)
            const submitErrMsg = (err && err.status === 400 && err.message)
              ? err.message
              : (err && err.status && err.message && err.status !== 500) ? `Error: ${err.status} - ${err.message}` : 'Record created but submit failed. Redirecting to edit.'
            showNotification('error', submitErrMsg)
            history.push(`${pathMap.UAE_MPCI_MASTER_BILL}/edit/${id}`, { submitErrorMsg: submitErrMsg })
          }
        )
      },
      (err) => {
        setSubmitting(false)
        const createErrMsg = (err && err.status && err.message && err.status !== 500) ? `Error: ${err.status} - ${err.message}` : 'Failed to create Master Bill. Please try again.'
        showNotification('error', createErrMsg)
      }
    )
  }, () => showNotification('error', 'Please fix all required fields before submitting.'))

    if (cloneLoading) {
    return <ParentCard title='Clone Master Bill of Lading'><Box p={4} display='flex' justifyContent='center'><CircularProgress /></Box></ParentCard>
  }

  return (
    <>
      <ParentCard title={cloneId ? 'Clone Master Bill of Lading' : 'Create Master Bill of Lading'}>
        <FormProvider {...methods}>
          <form>
            <FormProperties.Fields
              disabled={false}
              showNotification={showNotification}
              shouldHideMap={{}}
              isCreateMode={true}
              onDiscard={() => setDiscardDialog(true)}
              onSaveDraft={handleSaveDraft}
              onSubmitToNaic={handleSubmitToNaic}
              submitting={submitting}
            />
          </form>
        </FormProvider>
      </ParentCard>
      <ConfirmDialog
        isConfirmDialogOpen={discardDialog}
        closeDialog={() => setDiscardDialog(false)}
        confirmDialog={() => history.push(pathMap.UAE_MPCI_MASTER_BILL_LIST_VIEW)}
        content="All progress in this session will be lost and can't be restored. Are you sure about this?"
        okMsg='Yes, discard'
        cancelMsg='No, continue'
        title='Discard Changes'
      />
    </>
  )
}

export default AddPage
